// ID Verification Portal — Broker/Admin view
// Create link → View submissions → Review → Broker selfie → Generate PDF
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, ShieldCheck, Copy, Search, Link2, Camera, Download,
  Clock, MapPin, Fingerprint, CheckCircle2, AlertCircle, User, FileText,
  Trash2, RefreshCw, Plus, X, Eye, Loader2, XCircle, PenLine, Video, AlertTriangle,
  UserPlus, ArrowLeft, Send, Upload,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../hooks/useBranding';
import Header from '../components/Header';
import SignaturePad from '../components/SignaturePad';
// live-call removed — customer now uploads a recorded video instead
import BetaBadge from '../components/BetaBadge';
import { InviteDialog } from '../components/idverify/InviteDialog';
import { CameraCapture } from '../components/idverify/CameraCapture';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  link_created: { label: 'Link created', tone: 'bg-slate-100 text-slate-700 border-slate-300' },
  customer_started: { label: 'Customer started', tone: 'bg-amber-100 text-amber-800 border-amber-300' },
  customer_submitted: { label: 'Customer submitted', tone: 'bg-blue-100 text-blue-800 border-blue-300' },
  broker_reviewed: { label: 'Broker reviewed', tone: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  report_generated: { label: 'Report generated', tone: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const brokerDocsList = (doc) => {
  const parts = [];
  if (doc.id_type === 'passport' || (doc.passport && !doc.driver_licence)) parts.push('Passport');
  else {
    if (doc.driver_licence) parts.push('Driver Licence (front)');
    if (doc.driver_licence_back) parts.push('Driver Licence (back)');
  }
  if (doc.medicare_card) parts.push('Medicare Card');
  if (doc.selfie) parts.push('Live Selfie');
  return parts.join(', ') || 'the uploaded documents';
};

// ============================================================
const IDVerificationAdmin = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [autoGeneric, setAutoGeneric] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (search.trim()) params.search = search.trim();
      const { data } = await axios.get(`${API}/api/id-verify/verifications`, { headers: authHeaders, params });
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load verifications');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, filterStatus, search]);

  useEffect(() => { load(); }, [load]);

  const statusCounts = useMemo(() => {
    const c = {};
    items.forEach((it) => { c[it.status] = (c[it.status] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <div className="min-h-screen bg-page pb-16">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header row with back button */}
        <div className="flex items-start gap-3 mb-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center bg-white hover:bg-slate-100 transition-colors shrink-0 mt-1"
            data-testid="idverify-back"
            aria-label="Back to Home"
          >
            <ChevronLeft className="w-4 h-4 text-slate-700" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold uppercase tracking-wider text-emerald-600 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> High-security tool
            </p>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 mt-1 inline-flex items-center gap-2.5 flex-wrap">
              ID Verification Portal
              <BetaBadge size="sm" />
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Generate a unique customer link, review uploaded ID documents, then certify the review with your own selfie.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setProfileOpen(true)}
            data-testid="idverify-open-profile"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 hover:bg-slate-100 text-slate-800 text-sm font-semibold px-4 py-2.5 transition-colors"
          >
            <PenLine className="w-4 h-4" /> Verifier profile
          </button>
          <button
            type="button"
            onClick={() => { setAutoGeneric(true); setCreateOpen(true); }}
            data-testid="idverify-generic-link-btn"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 hover:bg-slate-100 text-slate-800 text-sm font-semibold px-4 py-2.5 transition-colors"
          >
            <Link2 className="w-4 h-4" /> Get shareable link
          </button>
          <button
            type="button"
            onClick={() => { setAutoGeneric(false); setCreateOpen(true); }}
            data-testid="idverify-create-link"
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2.5 shadow-sm transition-colors"
          >
            <Send className="w-4 h-4" /> Manual Invite
          </button>
        </div>

        {/* Status tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {Object.entries(STATUS_META).map(([k, meta]) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilterStatus(filterStatus === k ? '' : k)}
              data-testid={`idverify-status-${k}`}
              className={`text-left rounded-2xl border p-4 transition-all ${
                filterStatus === k
                  ? 'bg-slate-900 border-slate-900 text-white shadow'
                  : 'bg-white border-slate-200 hover:border-slate-400'
              }`}
            >
              <p className={`text-[10px] font-bold uppercase tracking-wider ${filterStatus === k ? 'text-white/70' : 'text-slate-500'}`}>
                {meta.label}
              </p>
              <p className={`text-2xl font-black tabular-nums mt-1 ${filterStatus === k ? 'text-white' : 'text-slate-900'}`}>
                {statusCounts[k] || 0}
              </p>
            </button>
          ))}
        </div>

        {/* Search bar */}
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or reference…"
              data-testid="idverify-search"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
            />
          </div>
          <button
            type="button"
            onClick={load}
            className="w-9 h-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-100"
            aria-label="Refresh"
            data-testid="idverify-refresh"
          >
            <RefreshCw className={`w-4 h-4 text-slate-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* List */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            <div className="col-span-3">Reference / Customer</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Created</div>
            <div className="col-span-2">Submitted</div>
            <div className="col-span-2">Reviewed</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {loading && items.length === 0 && (
            <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-10 text-center">
              <ShieldCheck className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No verifications yet. Create your first link to send to a customer.</p>
            </div>
          )}
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => setSelected(it)}
              data-testid={`idverify-row-${it.reference_number}`}
              className="w-full text-left grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
            >
              <div className="col-span-3 min-w-0">
                <div className="text-sm font-bold text-slate-900 truncate">
                  {it.first_name || it.last_name
                    ? `${it.first_name || ''} ${it.last_name || ''}`.trim()
                    : it.customer_hint || <span className="italic text-slate-400">Unnamed</span>}
                </div>
                <div className="text-[11px] font-mono text-slate-500 mt-0.5">{it.reference_number}</div>
              </div>
              <div className="col-span-2">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${STATUS_META[it.status]?.tone || 'bg-slate-100 text-slate-700 border-slate-300'}`}>
                  {STATUS_META[it.status]?.label || it.status}
                </span>
              </div>
              <div className="col-span-2 text-xs text-slate-600">{fmtDateTime(it.created_at)}</div>
              <div className="col-span-2 text-xs text-slate-600">{fmtDateTime(it.submitted_at)}</div>
              <div className="col-span-2 text-xs text-slate-600">{fmtDateTime(it.reviewed_at)}</div>
              <div className="col-span-1 flex items-center justify-end">
                <Eye className="w-4 h-4 text-slate-400" />
              </div>
            </button>
          ))}
        </div>
      </main>

      {createOpen && !autoGeneric && <InviteDialog headers={authHeaders} onClose={() => setCreateOpen(false)} onCreated={load} onGeneric={() => setAutoGeneric(true)} />}
      {createOpen && autoGeneric && (
        <CreateLinkDialog
          onClose={() => { setCreateOpen(false); setAutoGeneric(false); }}
          onCreated={() => { setCreateOpen(false); setAutoGeneric(false); load(); }}
          authHeaders={authHeaders}
          autoGeneric={autoGeneric}
        />
      )}
      {profileOpen && (
        <VerifierProfileDialog
          onClose={() => setProfileOpen(false)}
          authHeaders={authHeaders}
        />
      )}
      {selected && (
        <VerificationDetailDrawer
          verificationId={selected.id}
          onClose={() => setSelected(null)}
          onChanged={load}
          authHeaders={authHeaders}
        />
      )}
    </div>
  );
};

// ============================================================
const CreateLinkDialog = ({ onClose, onCreated, authHeaders, autoGeneric = false }) => {
  // Mode selection: 'choose' | 'generic' | 'client'
  const [mode, setMode] = useState('choose');
  const [hint, setHint] = useState('');
  const [prefill, setPrefill] = useState({
    first_name: '', last_name: '', date_of_birth: '', address: '', email: '', mobile: '',
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Validation for the "Add client" flow — need at least a first + last name so
  // the verification record is meaningfully tied to a specific customer.
  const clientReady = mode === 'client'
    && prefill.first_name.trim().length >= 1
    && prefill.last_name.trim().length >= 1;

  const submit = async () => {
    setBusy(true);
    try {
      // Generic mode → create a REUSABLE intake link anyone can open (each gets their own record).
      if (mode === 'generic') {
        const { data } = await axios.post(
          `${API}/api/id-verify/generic-link`,
          { label: hint.trim() || null },
          { headers: authHeaders },
        );
        setResult(data);
        toast.success('Generic verification link created — share it with anyone');
        return;
      }
      // For "Add client" flow, auto-generate a hint from name if broker didn't type one
      const autoHint = (mode === 'client' && !hint.trim())
        ? `${prefill.first_name.trim()} ${prefill.last_name.trim()}`.trim()
        : hint.trim();
      const { data } = await axios.post(
        `${API}/api/id-verify/create-link`,
        { customer_hint: autoHint || null },
        { headers: authHeaders },
      );
      // "Add client" mode → always save prefill (validated above)
      if (mode === 'client') {
        try {
          await axios.put(
            `${API}/api/id-verify/verifications/${data.id}/prefill`,
            prefill,
            { headers: authHeaders },
          );
        } catch (e) { /* non-fatal */ }
      }
      setResult(data);
      toast.success('Client added — verification link ready');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create link');
    } finally {
      setBusy(false);
    }
  };

  const customerUrl = result ? `${window.location.origin}${result.customer_url_path}` : '';

  const copy = () => {
    navigator.clipboard.writeText(customerUrl);
    toast.success('Link copied');
  };

  const back = () => {
    setMode('choose');
    setHint('');
    setPrefill({ first_name: '', last_name: '', date_of_birth: '', address: '', email: '', mobile: '' });
  };

  // Opened via the tab's one-click "Get shareable link" button → generate immediately.
  const runGeneric = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/id-verify/generic-link`, { label: null }, { headers: authHeaders });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create link');
      onClose();
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { if (autoGeneric) runGeneric(); /* eslint-disable-next-line */ }, [autoGeneric]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl border border-slate-200" data-testid="idverify-create-dialog">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            {mode !== 'choose' && !result && (
              <button
                type="button"
                onClick={back}
                data-testid="idverify-create-back"
                className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"
                aria-label="Back to mode selection"
              >
                <ArrowLeft className="w-4 h-4 text-slate-600" />
              </button>
            )}
            <h3 className="text-lg font-black text-slate-900">
              {result
                ? 'Shareable verification link'
                : autoGeneric
                  ? 'Creating shareable link…'
                  : mode === 'choose'
                    ? 'Create verification link'
                    : mode === 'generic'
                      ? 'Copy generic link'
                      : 'Add client'}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>

        <div className="p-5">
          {/* ---------- Auto generic (one-click from the tab) ---------- */}
          {!result && autoGeneric && (
            <div className="flex flex-col items-center justify-center gap-3 py-8 text-center" data-testid="idverify-generic-loading">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900" />
              <p className="text-sm font-semibold text-slate-600">Preparing your reusable link…</p>
            </div>
          )}
          {/* ---------- Step 0: mode chooser ---------- */}
          {!result && !autoGeneric && mode === 'choose' && (
            <>
              <p className="text-sm text-slate-600 mb-4">
                How would you like to create this verification link?
              </p>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setMode('generic')}
                  data-testid="idverify-mode-generic"
                  className="w-full text-left p-4 rounded-xl border-2 border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-slate-900 group-hover:text-white flex items-center justify-center flex-shrink-0 transition-colors">
                      <Link2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-black text-slate-900">Copy generic link</div>
                      <p className="text-xs text-slate-600 mt-1">
                        Creates one reusable link you can share with anyone. Each person who opens it starts their own separate verification and fills in their own details.
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setMode('client')}
                  data-testid="idverify-mode-client"
                  className="w-full text-left p-4 rounded-xl border-2 border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 group-hover:bg-emerald-600 group-hover:text-white flex items-center justify-center flex-shrink-0 transition-colors">
                      <UserPlus className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-black text-slate-900">Add client</div>
                      <p className="text-xs text-slate-600 mt-1">
                        Enter your customer&apos;s details first, then generate a link that&apos;s pre-populated and tied specifically to them.
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </>
          )}

          {/* ---------- Generic link flow ---------- */}
          {!result && mode === 'generic' && (
            <>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Optional label</label>
              <input
                type="text"
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                placeholder="e.g. Feb generic link"
                data-testid="idverify-create-hint"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
              />
              <p className="text-xs text-slate-500 mt-2">
                Helps you identify the link before the customer submits their details. Not shown to the customer.
              </p>

              <button
                type="button"
                onClick={submit}
                disabled={busy}
                data-testid="idverify-create-submit"
                className="w-full mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2.5 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Generate generic link
              </button>
            </>
          )}

          {/* ---------- Add client flow ---------- */}
          {!result && mode === 'client' && (
            <>
              <p className="text-xs text-slate-600 mb-3">
                Enter your customer&apos;s details. First and last name are required — everything else is optional and will pre-populate their form.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                    First name<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <input
                    type="text" value={prefill.first_name}
                    onChange={(e) => setPrefill({ ...prefill, first_name: e.target.value })}
                    placeholder="First name" data-testid="idverify-prefill-first-name"
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                    Last name<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <input
                    type="text" value={prefill.last_name}
                    onChange={(e) => setPrefill({ ...prefill, last_name: e.target.value })}
                    placeholder="Last name" data-testid="idverify-prefill-last-name"
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Date of birth</label>
                  <input
                    type="date" value={prefill.date_of_birth}
                    onChange={(e) => setPrefill({ ...prefill, date_of_birth: e.target.value })}
                    data-testid="idverify-prefill-dob"
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Mobile</label>
                  <input
                    type="tel" value={prefill.mobile}
                    onChange={(e) => setPrefill({ ...prefill, mobile: e.target.value })}
                    placeholder="Mobile" data-testid="idverify-prefill-mobile"
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  />
                </div>
              </div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mt-2 block">Email</label>
              <input
                type="email" value={prefill.email}
                onChange={(e) => setPrefill({ ...prefill, email: e.target.value })}
                placeholder="Email address" data-testid="idverify-prefill-email"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              />
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mt-2 block">Residential address</label>
              <input
                type="text" value={prefill.address}
                onChange={(e) => setPrefill({ ...prefill, address: e.target.value })}
                placeholder="Residential address" data-testid="idverify-prefill-address"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              />

              <div className="mt-4 border-t border-slate-100 pt-4">
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Optional broker label</label>
                <input
                  type="text"
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                  placeholder="e.g. John Smith - Refinance"
                  data-testid="idverify-create-hint"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
                />
                <p className="text-[11px] text-slate-500 mt-1">Defaults to the client&apos;s name if left blank.</p>
              </div>

              <button
                type="button"
                onClick={submit}
                disabled={busy || !clientReady}
                data-testid="idverify-create-submit"
                className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2.5 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Add client &amp; generate link
              </button>
              {!clientReady && (
                <p className="text-[11px] text-amber-700 mt-2 text-center" data-testid="idverify-client-validation">
                  First and last name are required.
                </p>
              )}
            </>
          )}

          {/* ---------- Result: link ready ---------- */}
          {result && (
            <>
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 mb-4">
                <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
                  <CheckCircle2 className="w-4 h-4" /> {result.generic ? 'Generic link ready — share it with anyone' : 'Link ready — copy and send to your customer'}
                </div>
                <div className="text-xs text-emerald-700 mt-1">
                  {result.generic
                    ? <>Reusable — every person who opens it starts their own separate verification.</>
                    : <>Reference: <span className="font-mono">{result.reference_number}</span> • Expires: {fmtDateTime(result.expires_at)}</>}
                </div>
              </div>
              <div className="flex items-stretch gap-2">
                <input
                  type="text"
                  readOnly
                  value={customerUrl}
                  data-testid="idverify-created-url"
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-xs font-mono text-slate-900 bg-slate-50 select-all"
                  onClick={(e) => e.target.select()}
                />
                <button
                  type="button"
                  onClick={copy}
                  data-testid="idverify-created-copy"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3"
                >
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-3">
                {result.generic
                  ? 'Share this link with anyone. Each person who opens it gets their own private, secure verification — every submission appears in your list.'
                  : 'This link is unique, unguessable, and single-use. It cannot be re-opened once the customer submits.'}
              </p>
              <button
                type="button"
                onClick={onCreated}
                className="w-full mt-4 rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-semibold px-4 py-2.5"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Verifier Profile — one saved profile per broker, auto-included in every
// PDF report generated by this user. Compliance mandate.
const ProfileInputRow = ({ label, value, onChange, placeholder, required, type = 'text', testId }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
    />
  </label>
);

const VerifierProfileDialog = ({ onClose, authHeaders }) => {
  const [profile, setProfile] = useState({
    legal_name: '', business_name: '', business_address: '', role: '', email: '', phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/id-verify/verifier-profile`, { headers: authHeaders });
        setProfile({
          legal_name: data.legal_name || '',
          business_name: data.business_name || '',
          business_address: data.business_address || '',
          role: data.role || '',
          email: data.email || '',
          phone: data.phone || '',
        });
      } catch (e) {
        toast.error('Failed to load verifier profile');
      } finally { setLoading(false); }
    })();
  }, [authHeaders]);

  const save = async () => {
    if (!profile.legal_name.trim()) {
      toast.error('Legal name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/api/id-verify/verifier-profile`, profile, { headers: authHeaders });
      toast.success('Verifier profile saved — will be included in every future report');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="idverify-profile-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">Verifier Profile</p>
            <h3 className="text-lg font-black text-slate-900">Your details for the compliance PDF</h3>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-500">
            These details will appear in the <b>Broker / Verifier Declaration</b> section of every PDF report you generate.
            Fill this in once — it applies to all future verifications.
          </p>
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <>
              <ProfileInputRow label="Legal full name" value={profile.legal_name} onChange={(v) => setProfile({ ...profile, legal_name: v })} placeholder="Alex Smith" required testId="idverify-profile-legal-name" />
              <ProfileInputRow label="Business name" value={profile.business_name} onChange={(v) => setProfile({ ...profile, business_name: v })} placeholder="Derwent Finance" testId="idverify-profile-business-name" />
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Business address</span>
                <textarea
                  rows={2}
                  value={profile.business_address}
                  onChange={(e) => setProfile({ ...profile, business_address: e.target.value })}
                  placeholder="Street, Suburb, State, Postcode"
                  data-testid="idverify-profile-business-address"
                  className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
                />
              </label>
              <ProfileInputRow label="Role / title" value={profile.role} onChange={(v) => setProfile({ ...profile, role: v })} placeholder="Director / VOI Officer" testId="idverify-profile-role" />
              <ProfileInputRow label="Verifier email" type="email" value={profile.email} onChange={(v) => setProfile({ ...profile, email: v })} placeholder="[email protected]" testId="idverify-profile-email" />
              <ProfileInputRow label="Verifier phone" type="tel" value={profile.phone} onChange={(v) => setProfile({ ...profile, phone: v })} placeholder="+61 400 000 000" testId="idverify-profile-phone" />
            </>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-semibold px-4 py-2">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading}
            data-testid="idverify-profile-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Save profile
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================
export const VerificationDetailDrawer = ({ verificationId, onClose, onChanged, authHeaders, fullScreen = false }) => {
  const brand = useBranding();
  const [doc, setDoc] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/id-verify/verifications/${verificationId}`, { headers: authHeaders });
      setDoc(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [verificationId, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const uploadBrokerSelfie = async (file) => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post(
        `${API}/api/id-verify/verifications/${verificationId}/broker-selfie`,
        form,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
      );
      setDoc(data);
      toast.success('Broker selfie uploaded — ready to generate report');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const generateReport = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/id-verify/verifications/${verificationId}/generate-report`, {}, { headers: authHeaders });
      toast.success('Report generated');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate report');
    } finally {
      setBusy(false);
    }
  };

  const uploadBrokerSignature = async (file) => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(
        `${API}/api/id-verify/verifications/${verificationId}/broker-signature`,
        form,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Broker signature captured');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Signature upload failed');
    } finally {
      setBusy(false);
    }
  };

  const downloadReport = async () => {
    const url = `${API}/api/id-verify/verifications/${verificationId}/report`;
    try {
      const resp = await fetch(url, { headers: authHeaders });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${doc?.reference_number || 'report'}_certification.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // release URL after a short delay so the browser has time to fetch it
      setTimeout(() => URL.revokeObjectURL(objUrl), 3000);
    } catch (e) {
      toast.error('Failed to download report');
    }
  };

  const deleteRecord = async () => {
    if (!window.confirm('Soft-delete this verification record? Files remain in secure storage.')) return;
    try {
      await axios.delete(`${API}/api/id-verify/verifications/${verificationId}`, { headers: authHeaders });
      toast.success('Deleted');
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const regenerateLink = async () => {
    if (!window.confirm('Regenerate the customer link? The old URL will stop working immediately.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/id-verify/verifications/${verificationId}/regenerate-link`, {}, { headers: authHeaders });
      toast.success('Link regenerated — copy the new URL below');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Regenerate failed');
    } finally { setBusy(false); }
  };

  const disableLink = async () => {
    if (!window.confirm('Disable this customer link? The customer will no longer be able to submit.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/id-verify/verifications/${verificationId}/disable-link`, {}, { headers: authHeaders });
      toast.success('Link disabled');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disable failed');
    } finally { setBusy(false); }
  };

  // Broker uploads an ID document on the customer's behalf. Customer flow
  // then auto-skips that step. Only allowed before customer_submitted.
  const brokerUploadDoc = async (fileType, file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(
        `${API}/api/id-verify/verifications/${verificationId}/broker-upload/${fileType}`,
        fd,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`${fileType.replace(/_/g, ' ')} uploaded on behalf of customer`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-stretch justify-end">
      <CameraCapture open={camOpen} onClose={() => setCamOpen(false)} onCapture={uploadBrokerSelfie} title="Broker confirmation selfie" />
      <div className={`bg-page w-full overflow-y-auto shadow-2xl ${fullScreen ? 'max-w-none' : 'max-w-2xl border-l border-slate-200'}`} data-testid="idverify-detail">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 font-mono">
              {doc?.reference_number || '…'}
            </p>
            <h3 className="text-lg font-black text-slate-900 truncate">
              {doc?.first_name || doc?.last_name
                ? `${doc?.first_name || ''} ${doc?.last_name || ''}`.trim()
                : doc?.customer_hint || 'Unnamed verification'}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="idverify-detail-close">
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>

        {loading || !doc ? (
          <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
        ) : (
          <div className={`p-5 space-y-5 ${fullScreen ? 'mx-auto max-w-5xl' : ''}`}>
            {/* Status pill */}
            <div>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${STATUS_META[doc.status]?.tone}`}>
                {STATUS_META[doc.status]?.label || doc.status}
              </span>
            </div>

            {/* Customer link (still valid?) — always show while pre-submit or if disabled */}
            {(doc.status === 'link_created' || doc.status === 'customer_started' || doc.link_disabled_at) ? (
              <CustomerLinkBox
                token={doc.token}
                expiresAt={doc.expires_at}
                disabled={!!doc.link_disabled_at}
                onRegenerate={regenerateLink}
                onDisable={disableLink}
              />
            ) : null}

            {/* Customer details */}
            <Section title="Customer details" icon={User}>
              <KV label="Full name" value={`${doc.first_name || ''} ${doc.last_name || ''}`.trim() || '—'} />
              <KV label="Date of birth" value={doc.date_of_birth || '—'} />
              <KV label="Residential address" value={doc.address || '—'} />
              <KV label="Customer IP" value={doc.customer_ip || '—'} mono />
              <KV label="User agent" value={doc.customer_user_agent || '—'} small />
              <KV label="Consent accepted" value={doc.consent_accepted_at ? `${fmtDateTime(doc.consent_accepted_at)} · ${doc.consent_ip || 'IP unknown'}` : '—'} small />
            </Section>

            {/* Broker upload — pre-load customer's ID docs on their behalf */}
            {(doc.status === 'link_created' || doc.status === 'customer_started') && (
              <Section title="Upload documents on customer's behalf" icon={Camera}>
                <p className="text-xs text-slate-600 mb-3">
                  If you already have the customer&apos;s documents on file, upload them here.
                  The customer&apos;s portal will automatically skip any step you complete.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { key: 'driver_licence', label: 'Driver licence — FRONT' },
                    { key: 'driver_licence_back', label: 'Driver licence — BACK' },
                    { key: 'passport', label: 'Passport (ID page)' },
                    { key: 'medicare_card', label: 'Medicare card' },
                  ].map((slot) => {
                    const ref = doc[slot.key];
                    const isBroker = ref?.uploaded_by_role === 'broker';
                    return (
                      <label
                        key={slot.key}
                        className={`rounded-xl border p-3 cursor-pointer transition-colors ${
                          isBroker ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200 hover:border-slate-400'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-900">{slot.label}</p>
                            <p className={`text-[10px] mt-0.5 ${isBroker ? 'text-emerald-700' : 'text-slate-500'}`}>
                              {isBroker ? 'Uploaded by broker ✓ customer will skip this step' : ref ? 'Uploaded by customer' : 'Not uploaded — tap to upload'}
                            </p>
                          </div>
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /> : <Camera className="w-3.5 h-3.5 text-slate-400" />}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          data-testid={`idverify-broker-upload-${slot.key}`}
                          disabled={busy}
                          onChange={(e) => brokerUploadDoc(slot.key, e.target.files?.[0])}
                        />
                      </label>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* Uploads */}
            <Section title="Uploaded documents" icon={FileText}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <FileTile label="Customer selfie (legacy)" fileRef={doc.selfie} vid={doc.id} kind="selfie" authHeaders={authHeaders} />
                {doc.id_type === 'passport' || (doc.passport && !doc.driver_licence) ? (
                  <FileTile label="Passport" fileRef={doc.passport} vid={doc.id} kind="passport" authHeaders={authHeaders} />
                ) : (
                  <>
                    <FileTile label="Driver licence (front)" fileRef={doc.driver_licence} vid={doc.id} kind="driver_licence" authHeaders={authHeaders} />
                    <FileTile label="Driver licence (back)" fileRef={doc.driver_licence_back} vid={doc.id} kind="driver_licence_back" authHeaders={authHeaders} />
                  </>
                )}
                <FileTile label="Medicare card" fileRef={doc.medicare_card} vid={doc.id} kind="medicare_card" authHeaders={authHeaders} />
              </div>
            </Section>

            {/* Live Video Verification — reuses WebRTC stack + face-api.js */}
            <Section title="Customer video recording" icon={Video}>
              <div className="rounded-xl border border-slate-200 bg-white p-3">
                <p className="text-xs text-slate-600 mb-3">
                  Watch the customer&apos;s short verification video below. Confirm their face matches the ID
                  they&apos;re holding next to their face, then upload your broker confirmation selfie and
                  generate the certified PDF report.
                </p>
                {doc.recorded_video ? (
                  <VideoPlayerTile
                    vid={doc.id}
                    fileRef={doc.recorded_video}
                    authHeaders={authHeaders}
                  />
                ) : (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                    <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                    No video recording yet — waiting for the customer to complete their submission.
                  </div>
                )}
              </div>
            </Section>

            {/* Broker selfie + declaration + generate */}
            <Section title="Broker certification" icon={Fingerprint}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FileTile label="Broker confirmation selfie" fileRef={doc.broker_selfie} vid={doc.id} kind="broker_selfie" authHeaders={authHeaders} />
                <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Actions</p>
                    <p className="text-xs text-slate-600 mb-3">
                      After reviewing the customer&apos;s ID above, upload your own selfie, sign the broker declaration below, then generate the report.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setCamOpen(true)}
                      disabled={busy || !['customer_submitted', 'broker_reviewed', 'report_generated'].includes(doc.status)}
                      data-testid="idverify-broker-selfie-camera"
                      className="inline-flex items-center justify-center gap-1.5 w-full rounded-full text-xs font-semibold px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white disabled:bg-slate-100 disabled:text-slate-400"
                    >
                      <Camera className="w-3.5 h-3.5" /> {doc.broker_selfie ? 'Retake selfie with camera' : 'Take selfie with camera'}
                    </button>
                    <label
                      className={`inline-flex items-center justify-center gap-1.5 w-full rounded-full text-xs font-semibold px-3 py-2 cursor-pointer transition-colors border ${
                        doc.status === 'customer_submitted' || doc.status === 'broker_reviewed' || doc.status === 'report_generated'
                          ? 'border-slate-300 bg-white hover:border-slate-500 text-slate-800'
                          : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      <Upload className="w-3.5 h-3.5" /> {doc.broker_selfie ? 'Replace with a file' : 'Upload a file'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid="idverify-broker-selfie-input"
                        disabled={busy || !(doc.status === 'customer_submitted' || doc.status === 'broker_reviewed' || doc.status === 'report_generated')}
                        onChange={(e) => uploadBrokerSelfie(e.target.files?.[0])}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={generateReport}
                      disabled={busy || !(doc.status === 'broker_reviewed' || doc.status === 'report_generated') || !doc.broker_signature || !doc.recorded_video}
                      data-testid="idverify-generate-report"
                      className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                      Generate Report
                    </button>
                    {!doc.broker_signature && (doc.status === 'broker_reviewed' || doc.status === 'customer_submitted') && (
                      <p className="text-[10px] text-amber-700 text-center">Broker declaration & signature required before generating.</p>
                    )}
                    {!doc.recorded_video && (
                      <p className="text-[10px] text-amber-700 text-center" data-testid="idverify-needs-video">Customer video recording required before generating.</p>
                    )}
                    {doc.report_pdf && (
                      <button
                        type="button"
                        onClick={downloadReport}
                        data-testid="idverify-download-report"
                        className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-300 hover:bg-slate-50 text-slate-800 text-xs font-semibold px-3 py-2"
                      >
                        <Download className="w-3.5 h-3.5" /> Download PDF
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Broker Declaration + signature — only shown once customer has submitted */}
              {(doc.status === 'customer_submitted' || doc.status === 'broker_reviewed' || doc.status === 'report_generated') && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1.5">
                    <PenLine className="w-3.5 h-3.5" /> Broker / Verifier Declaration
                  </p>
                  <p className="text-xs text-slate-800 leading-relaxed">
                    I, <b>{doc.reviewed_by_name || doc.created_by_name || 'Broker'}</b>, being a representative of {brand.business_name || 'the brokerage'}, hereby certify that:
                  </p>
                  <ul className="text-xs text-slate-700 leading-relaxed mt-2 space-y-1 ml-4 list-[lower-alpha]">
                    <li>I have taken reasonable steps to ensure the identification documents relate to <b>{doc.first_name} {doc.last_name}</b>;</li>
                    <li>The identification was carried out remotely on <b>{new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' })}</b>; and</li>
                    <li>The identity documents provided to me as electronic copies were <b>{brokerDocsList(doc)}</b>.</li>
                  </ul>
                  <div className="mt-3">
                    <SignaturePad
                      onSaved={uploadBrokerSignature}
                      saving={busy}
                      savedAt={doc.broker_signature?.uploaded_at}
                      testIdPrefix="idverify-broker-sig"
                      height={140}
                    />
                    {doc.broker_signature && (
                      <p className="text-[11px] text-emerald-700 font-semibold mt-1 inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Signed {fmtDateTime(doc.broker_signature.uploaded_at)}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </Section>

            {/* Activity */}
            <Section title="Activity timeline" icon={Clock}>
              <ol className="space-y-2 text-xs">
                {[...(doc.activity || [])].reverse().map((a, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 bg-white">
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      <Fingerprint className="w-3.5 h-3.5 text-slate-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{a.action}</span>
                        <span className="text-slate-500">{fmtDateTime(a.ts)}</span>
                      </div>
                      <div className="text-slate-500 mt-0.5">
                        {a.actor === 'broker' ? `Broker: ${a.actor_name || 'system'}` : 'Customer'}
                        {a.ip ? ` • IP ${a.ip}` : ''}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </Section>

            {/* Danger zone */}
            <div className="pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={deleteRecord}
                data-testid="idverify-delete"
                className="inline-flex items-center gap-1.5 rounded-lg text-red-600 hover:bg-red-50 text-xs font-semibold px-3 py-2 border border-red-200"
              >
                <Trash2 className="w-3.5 h-3.5" /> Soft-delete verification
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CustomerLinkBox = ({ token, expiresAt, disabled, onRegenerate, onDisable }) => {
  const url = `${window.location.origin}/verify/${token}`;
  const copy = () => { navigator.clipboard.writeText(url); toast.success('Link copied'); };
  return (
    <div className={`rounded-xl border p-3 ${disabled ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${disabled ? 'text-red-700' : 'text-emerald-700'}`}>
          <Link2 className="w-3.5 h-3.5" /> {disabled ? 'Link DISABLED' : 'Customer link (send manually)'}
        </span>
        <span className={`text-[10px] ${disabled ? 'text-red-700' : 'text-emerald-700'}`}>
          {disabled ? 'Customer cannot submit' : `Expires ${fmtDateTime(expiresAt)}`}
        </span>
      </div>
      <div className="flex items-stretch gap-2">
        <input
          type="text"
          readOnly
          value={url}
          data-testid="idverify-customer-link"
          onClick={(e) => e.target.select()}
          className={`flex-1 px-3 py-2 rounded-lg border text-[11px] font-mono text-slate-900 bg-white select-all ${disabled ? 'border-red-300 line-through' : 'border-emerald-300'}`}
        />
        <button
          type="button"
          onClick={copy}
          disabled={disabled}
          data-testid="idverify-copy-customer-link"
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 disabled:opacity-40"
        >
          <Copy className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <button
          type="button"
          onClick={onRegenerate}
          data-testid="idverify-regenerate-link"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-800 text-[11px] font-semibold px-2.5 py-1.5"
        >
          <RefreshCw className="w-3 h-3" /> Regenerate
        </button>
        {!disabled && (
          <button
            type="button"
            onClick={onDisable}
            data-testid="idverify-disable-link"
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 hover:bg-red-100 text-red-700 text-[11px] font-semibold px-2.5 py-1.5"
          >
            <XCircle className="w-3 h-3" /> Disable link
          </button>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <h4 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">
      <Icon className="w-3.5 h-3.5" /> {title}
    </h4>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const KV = ({ label, value, mono, small }) => (
  <div className="flex items-start gap-3 py-1.5 border-b border-slate-100 last:border-0">
    <div className="w-40 text-[11px] font-bold uppercase tracking-wider text-slate-500 shrink-0">{label}</div>
    <div className={`flex-1 text-slate-900 ${mono ? 'font-mono text-xs' : ''} ${small ? 'text-xs' : 'text-sm'} break-all`}>{value}</div>
  </div>
);

// FileTile — loads image via authenticated blob fetch (no token in URL)

// Video player tile — used to stream the customer's recorded verification video.
// Fetches the video via the authenticated broker file endpoint and mounts it in a <video>.
const VideoPlayerTile = ({ vid, fileRef, authHeaders }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        setLoading(true);
        const resp = await fetch(`${API}/api/id-verify/verifications/${vid}/file/recorded_video`, {
          headers: authHeaders,
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setVideoUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load video');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [vid, authHeaders]);

  const sizeMb = ((fileRef?.size || 0) / (1024 * 1024)).toFixed(2);
  const uploader = fileRef?.uploaded_by_role || 'customer';

  return (
    <div className="rounded-lg bg-slate-900 overflow-hidden" data-testid="idverify-video-player">
      {loading && (
        <div className="aspect-video flex items-center justify-center text-slate-300 text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Loading video…
        </div>
      )}
      {!loading && error && (
        <div className="aspect-video flex items-center justify-center text-red-300 text-xs p-4 text-center">
          Failed to load video: {error}
        </div>
      )}
      {!loading && videoUrl && (
        <video src={videoUrl} controls playsInline className="w-full h-auto" data-testid="idverify-video-el" />
      )}
      <div className="bg-slate-800 text-slate-300 text-[10px] font-mono px-2 py-1.5 flex items-center justify-between">
        <span>REC · {fmtDateTime(fileRef?.uploaded_at)}</span>
        <span>{sizeMb} MB · uploaded by {uploader}</span>
      </div>
    </div>
  );
};


const FileTile = ({ label, fileRef, vid, kind, authHeaders }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!fileRef) return;
    let cancelled = false;
    let currentUrl = null;
    (async () => {
      try {
        const resp = await fetch(`${API}/api/id-verify/verifications/${vid}/file/${kind}`, { headers: authHeaders });
        if (!resp.ok) return;
        const blob = await resp.blob();
        if (cancelled) return;
        currentUrl = URL.createObjectURL(blob);
        setBlobUrl(currentUrl);
      } catch { /* noop */ }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [fileRef, vid, kind, authHeaders]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="aspect-square bg-slate-100 flex items-center justify-center">
        {fileRef && blobUrl ? (
          <img src={blobUrl} alt={label} className="w-full h-full object-cover" data-testid={`idverify-file-${kind}`} />
        ) : fileRef ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        ) : (
          <div className="text-center text-slate-400 text-xs px-2">
            <AlertCircle className="w-5 h-5 mx-auto mb-1" />
            Not uploaded
          </div>
        )}
      </div>
      <div className="p-2.5">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-[10px] text-slate-500 mt-0.5">
          {fileRef ? fmtDateTime(fileRef.uploaded_at) : '—'}
        </p>
      </div>
    </div>
  );
};

export default IDVerificationAdmin;
